<template>
  <section class="coach-panel">
    <div class="coach-heading">
      <div>
        <p class="coach-eyebrow">Agent Coach</p>
        <h2>Improve this agent</h2>
        <p>Review behavior, find issues, and apply selected changes after approval.</p>
      </div>
      <button class="icon-button" type="button" @click="loadContext" :disabled="loadingContext" title="Refresh context">
        <RefreshCcw :size="16" :class="{ spin: loadingContext }" />
      </button>
    </div>

    <div v-if="contextSummary" class="context-strip">
      <span>{{ contextSummary.industry }}</span>
      <span>{{ contextSummary.language }}</span>
      <span>{{ contextSummary.sessions }}</span>
      <span>{{ contextSummary.signals }}</span>
    </div>

    <div class="quick-prompts">
      <button
        v-for="prompt in quickPrompts"
        :key="prompt"
        type="button"
        @click="sendQuickPrompt(prompt)"
        :disabled="sending"
      >
        {{ prompt }}
      </button>
    </div>

    <div class="thread" ref="threadEl">
      <div
        v-for="item in messages"
        :key="item.id"
        class="bubble"
        :class="item.role"
      >
        <p>{{ item.text }}</p>

        <div v-if="item.findings?.length" class="coach-list">
          <strong>Findings</strong>
          <ul>
            <li v-for="finding in item.findings" :key="finding">{{ finding }}</li>
          </ul>
        </div>

        <div v-if="item.suggestions?.length" class="coach-list">
          <strong>Suggestions</strong>
          <ul>
            <li v-for="suggestion in item.suggestions" :key="suggestion">{{ suggestion }}</li>
          </ul>
        </div>
      </div>

      <div v-if="sending" class="bubble assistant pending">
        <Loader2 :size="16" class="spin" />
        Reviewing agent configuration...
      </div>
    </div>

    <div v-if="proposedChanges.length" class="proposal-box">
      <div class="proposal-heading">
        <div>
          <h3>Proposed changes</h3>
          <p>These changes will update your agent configuration.</p>
        </div>
        <span>{{ proposedChanges.length }} change{{ proposedChanges.length === 1 ? '' : 's' }}</span>
      </div>

      <label
        v-for="change in proposedChanges"
        :key="change.field"
        class="diff-card"
      >
        <input type="checkbox" :value="change.field" v-model="selectedFields" />
        <div>
          <div class="diff-topline">
            <strong>{{ fieldLabel(change.field) }}</strong>
            <span :class="['risk', change.risk_level]">{{ change.risk_level }}</span>
          </div>
          <div class="diff-grid">
            <div>
              <span>Current</span>
              <p>{{ change.current_value_summary }}</p>
            </div>
            <div>
              <span>Proposed</span>
              <p>{{ summarizeProposed(change.proposed_value) }}</p>
            </div>
          </div>
          <small>{{ change.reason }}</small>
        </div>
      </label>

      <div class="proposal-actions">
        <button class="ghost-btn" type="button" @click="rejectChanges" :disabled="applying">
          <X :size="16" />
          Reject
        </button>
        <button class="primary-btn" type="button" @click="applySelectedChanges" :disabled="applying || selectedChanges.length === 0">
          <Check :size="16" />
          {{ applying ? 'Applying...' : 'Apply selected changes' }}
        </button>
      </div>
    </div>

    <form class="coach-input" @submit.prevent="sendMessage">
      <input
        v-model.trim="draft"
        placeholder="Ask: Review this agent, make it more professional, or improve the greeting"
        :disabled="sending"
      />
      <button class="primary-btn compact" type="submit" :disabled="sending || !draft">
        <Send :size="16" />
      </button>
    </form>

    <p v-if="error" class="coach-error">{{ error }}</p>
    <p v-if="success" class="coach-success">{{ success }}</p>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Check, Loader2, RefreshCcw, Send, X } from 'lucide-vue-next'
import * as api from '@/services/api.js'

const props = defineProps({
  agentId: {
    type: [Number, String],
    required: true
  }
})

const emit = defineEmits(['applied'])

const quickPrompts = [
  'Review this agent',
  'Make it more professional',
  'Make it shorter and more direct',
  'Why does it get stuck?'
]

const context = ref(null)
const messages = ref([])
const proposedChanges = ref([])
const selectedFields = ref([])
const draft = ref('')
const error = ref('')
const success = ref('')
const sending = ref(false)
const applying = ref(false)
const loadingContext = ref(false)
const threadEl = ref(null)

const contextSummary = computed(() => {
  if (!context.value) return null
  const agent = context.value.agent || {}
  const sessions = context.value.recent_sessions || []
  const signalCount = sessions.reduce((count, session) => count + (session.workflow_signals?.length || 0), 0)

  return {
    industry: agent.industry_key || 'generic',
    language: agent.default_language || 'language not set',
    sessions: `${sessions.length} recent sessions`,
    signals: signalCount ? `${signalCount} behavior signals` : 'no recent signals'
  }
})

const selectedChanges = computed(() => {
  const selected = new Set(selectedFields.value)
  return proposedChanges.value.filter(change => selected.has(change.field))
})

onMounted(loadContext)

watch(() => props.agentId, () => {
  messages.value = []
  proposedChanges.value = []
  selectedFields.value = []
  loadContext()
})

async function loadContext() {
  if (!props.agentId) return
  loadingContext.value = true
  error.value = ''

  try {
    const res = await api.agents.coach.context(props.agentId)
    context.value = res.context
  } catch (err) {
    error.value = err.message || 'Unable to load coach context.'
  } finally {
    loadingContext.value = false
  }
}

async function sendQuickPrompt(prompt) {
  draft.value = prompt
  await sendMessage()
}

async function sendMessage() {
  const text = draft.value.trim()
  if (!text || !props.agentId) return

  error.value = ''
  success.value = ''
  sending.value = true
  draft.value = ''
  addMessage({ role: 'user', text })

  try {
    const res = await api.agents.coach.message(props.agentId, { message: text })
    context.value = res.context || context.value
    proposedChanges.value = Array.isArray(res.proposed_changes) ? res.proposed_changes : []
    selectedFields.value = proposedChanges.value.map(change => change.field)

    addMessage({
      role: 'assistant',
      text: res.message || 'I reviewed the agent.',
      findings: res.findings || [],
      suggestions: res.suggestions || []
    })
  } catch (err) {
    error.value = err.message || 'Unable to review this agent.'
  } finally {
    sending.value = false
  }
}

async function applySelectedChanges() {
  if (!selectedChanges.value.length) return

  applying.value = true
  error.value = ''
  success.value = ''

  try {
    const res = await api.agents.coach.apply(props.agentId, {
      confirmed: true,
      proposed_changes: selectedChanges.value
    })

    context.value = res.context || context.value
    proposedChanges.value = []
    selectedFields.value = []
    success.value = 'Selected changes applied.'
    addMessage({
      role: 'assistant',
      text: 'I applied the selected changes to this agent.'
    })
    emit('applied')
  } catch (err) {
    error.value = err.message || 'Unable to apply selected changes.'
  } finally {
    applying.value = false
  }
}

function rejectChanges() {
  proposedChanges.value = []
  selectedFields.value = []
  success.value = 'Proposed changes rejected.'
}

function addMessage(message) {
  messages.value.push({
    id: `${Date.now()}-${messages.value.length}`,
    ...message
  })

  nextTick(() => {
    if (threadEl.value) {
      threadEl.value.scrollTop = threadEl.value.scrollHeight
    }
  })
}

function summarizeProposed(value) {
  if (value === null || value === undefined || value === '') return '(empty)'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function fieldLabel(field) {
  return {
    name: 'Agent name',
    description: 'Description',
    business_name: 'Business name',
    industry_key: 'Industry profile',
    default_language: 'Default language',
    allowed_languages: 'Allowed languages',
    booking_enabled: 'Booking enabled',
    primary_goal: 'Primary goal',
    handoff_rules: 'Handoff rules',
    voice_style: 'Voice style',
    tone: 'Tone',
    greeting: 'Opening greeting',
    opening_greeting_template: 'Opening greeting',
    fallback_message: 'Fallback message',
    handoff_message: 'Handoff message',
    human_routing_message: 'Handoff message',
    instructions: 'Instructions',
    prompt: 'Instructions',
    behavior: 'Instructions',
    booking_ai_instruction: 'Booking instructions',
    ai_instruction: 'Booking instructions',
    booking_timezone: 'Booking timezone',
    timezone: 'Booking timezone',
    collect_name: 'Collect caller name',
    collect_phone: 'Collect caller phone',
    collect_email: 'Collect caller email'
  }[field] || field
}
</script>

<style scoped>
.coach-panel{display:grid;gap:16px;background:#fff;border:1px solid #e5e7eb;border-radius:12px;box-shadow:0 18px 50px rgba(15,23,42,.05);padding:22px}.coach-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}.coach-eyebrow{margin:0 0 6px;color:#0d8f62;font-size:12px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}.coach-heading h2,.coach-heading p{margin:0}.coach-heading h2{font-size:1.3rem}.coach-heading p{margin-top:5px;color:#66736b;line-height:1.5}.icon-button{width:38px;height:38px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #dfe7e1;border-radius:8px;background:#fff;color:#17211b;cursor:pointer}.context-strip{display:flex;gap:8px;flex-wrap:wrap}.context-strip span{border-radius:999px;background:#eef2f0;color:#526159;padding:7px 10px;font-size:12px;font-weight:900}.quick-prompts{display:flex;flex-wrap:wrap;gap:8px}.quick-prompts button{border:1px solid #dfe7e1;border-radius:8px;background:#fbfdfb;color:#17211b;padding:9px 11px;font-weight:900;cursor:pointer}.thread{display:grid;gap:10px;max-height:340px;overflow:auto;padding:4px}.bubble{max-width:88%;border-radius:10px;padding:12px 13px;line-height:1.5}.bubble p{margin:0}.bubble.user{justify-self:end;background:#17211b;color:#fff}.bubble.assistant{justify-self:start;background:#f3f6f4;color:#17211b}.bubble.pending{display:flex;align-items:center;gap:8px;color:#66736b}.coach-list{margin-top:10px}.coach-list strong{display:block;font-size:12px;text-transform:uppercase;color:#526159}.coach-list ul{margin:6px 0 0;padding-left:18px}.proposal-box{display:grid;gap:12px;border-top:1px solid #e6ece8;padding-top:16px}.proposal-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.proposal-heading h3,.proposal-heading p{margin:0}.proposal-heading h3{font-size:1.05rem}.proposal-heading p{margin-top:4px;color:#66736b}.proposal-heading span{border-radius:999px;background:#d1fae5;color:#047857;padding:6px 10px;font-size:12px;font-weight:900}.diff-card{display:grid;grid-template-columns:auto 1fr;gap:12px;border:1px solid #e1e8e3;border-radius:10px;padding:13px;background:#fbfdfb}.diff-card input{width:18px;height:18px;margin-top:2px;accent-color:#0d8f62}.diff-topline{display:flex;align-items:center;justify-content:space-between;gap:10px}.risk{border-radius:999px;padding:4px 8px;font-size:11px;font-weight:900;text-transform:uppercase;background:#eef2f0;color:#526159}.risk.low{background:#d1fae5;color:#047857}.risk.medium{background:#fef3c7;color:#92400e}.risk.high{background:#fee2e2;color:#b91c1c}.diff-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px}.diff-grid div{border:1px solid #e6ece8;border-radius:8px;background:#fff;padding:10px;min-width:0}.diff-grid span{display:block;color:#66736b;font-size:11px;font-weight:900;text-transform:uppercase}.diff-grid p{margin:5px 0 0;white-space:pre-wrap;overflow-wrap:anywhere;color:#17211b}.diff-card small{display:block;margin-top:9px;color:#66736b;line-height:1.45}.proposal-actions,.coach-input{display:flex;gap:10px;align-items:center}.proposal-actions{justify-content:flex-end}.coach-input input{flex:1;min-width:0;border:1px solid #d7e0da;border-radius:8px;background:#fff;color:#17211b;padding:12px 13px;font:inherit;outline:none}.primary-btn,.ghost-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;border-radius:8px;padding:0 15px;border:none;font-weight:900;cursor:pointer}.primary-btn{background:#17211b;color:#fff}.ghost-btn{background:#fff;color:#17211b;border:1px solid #dfe7e1}.compact{width:44px;padding:0}.primary-btn:disabled,.ghost-btn:disabled,.quick-prompts button:disabled,.icon-button:disabled{opacity:.6;cursor:not-allowed}.coach-error,.coach-success{margin:0;font-weight:900}.coach-error{color:#b91c1c}.coach-success{color:#047857}.spin{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}@media(max-width:720px){.diff-grid{grid-template-columns:1fr}.proposal-actions,.coach-input{align-items:stretch}.proposal-actions{flex-direction:column}.proposal-actions .primary-btn,.proposal-actions .ghost-btn{width:100%}.bubble{max-width:100%}}
</style>
